<template>
  <div>
    <div class="jumbotron pt-0">
      <p>
        All images you have uploaded are listed below. Click on one to open it in a new tab. You can also easily copy
        its URL or delete an image if you no longer need it.
      </p>
      <div class="well">
        <upload-gallery-image-button :profile-locked="profileLocked" :profile-approved="profileApproved"></upload-gallery-image-button>
      </div>
    </div>

    <div class="gallery-images" :style="{'max-width': `${maxWidth}px`}">
      <gallery-image-card
        v-for="image in images"
        :key="image.uuid"
        :deleting="!!deletingMap[image.id]"
        :image="image"
        @delete="onConfirmDelete(image)"
      ></gallery-image-card>
    </div>
    <img v-if="loading" src="/wp-content/themes/snappy-theme/assets/images/loading2.gif" class="loading-image" />

    <delete-image-modal :image="deletedImage" @delete="onDelete(deletedImage)" @hide="onHideModal"></delete-image-modal>
  </div>
</template>

<script>
import DeleteImageModal from '../components/account/gallery/DeleteImageModal';
import GalleryImageCard from '../components/account/gallery/GalleryImageCard';
import UploadGalleryImageButton from '../components/account/gallery/UploadGalleryImageButton';
import {getAllGalleryImages} from '../utility/api/gallery/getAllGalleryImages';
import {deleteGalleryImage} from '../utility/api/gallery/deleteGalleryImage';
import {showProfileLockedWarning} from '../utility/showProfileLockedWarning';

export default {
  name: 'account-gallery-page',
  components: {DeleteImageModal, GalleryImageCard, UploadGalleryImageButton},
  props: {
    profileLocked: {
      type: Boolean,
      required: true,
      default: false,
    },
    profileApproved: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return {
      deletingMap: {},
      deletedImage: null,
      images: [],
      loading: false,
      nextPage: 1,
      totalCount: 0,
    };
  },
  computed: {
    maxWidth() {
      return this.images.length * 400;
    },
  },
  methods: {
    async loadMoreImages() {
      this.loading = true;

      const {data, meta} = await getAllGalleryImages(this.nextPage);

      this.images = [...this.images, ...data];
      this.nextPage += 1;
      this.totalCount = meta.total_count;

      this.loading = false;
    },
    onConfirmDelete(image) {
      showProfileLockedWarning.warning(this.profileLocked).then(() => {
        this.deletedImage = image;
      });
    },
    async onDelete(image) {
      this.setImageDeleting(image, true);

      try {
        await deleteGalleryImage(image.id);
        $.notify('Image has been deleted.', 'success');
        this.images = [...this.images].filter(img => img.id !== image.id);
      } catch (error) {
        $.notify('Failed to delete the image.', 'error');
      }

      this.setImageDeleting(image, false);
    },
    onHideModal() {
      this.deletedImage = null;
    },
    setImageDeleting(image, deleting) {
      this.deletingMap = {...this.deletingMap, [image.id]: deleting};
    },
    activateInfiniteScroll() {
      const intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && this.images.length < this.totalCount && !this.loading) {
            this.loadMoreImages();
          }
        },
        {rootMargin: '400px'}
      );

      const [footer] = document.getElementsByTagName('footer');
      intersectionObserver.observe(footer);
    },
  },
  mounted() {
    this.loadMoreImages();
    this.activateInfiniteScroll();
  },
};
</script>

<style lang="scss" scoped>
.gallery-images {
    display: grid;
    gap: 20px;
    /* autoprefixer: ignore next */
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
}

.loading-image {
  display: inline-block;
  margin: 20px auto;
  width: 100px;
}
</style>
